import * as React from "react";
import { Chip, ChipProps } from "@mui/material";
import { GridRenderCellParams } from "@mui/x-data-grid";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { red } from "@mui/material/colors";
import AddCourse from "../../components/addNewCourse/addCourse";
import ReportOutlinedIcon from "@mui/icons-material/ReportOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import { ERROR_TOAST, INFO_TOAST, SUCCESS_TOAST } from "../../helpers/toasters";
import { getCourses, getCoursesBysearch, deleteCourse } from "../../API/courses";
import MainTable from "../../components/Datagrid/dataGridTable";
import { useSelector } from "react-redux";

export type CourseData = {
  _id: string;
  name: string;
  course_id: string;
  active: boolean;
  assessment: boolean;
  assessment_levels: string[];
  certification_levels: string[];
  course_completion: boolean;
  sample_report_card: string;
  sample_certificate: string;
  report_card: boolean;
  certificate: boolean;
  testimonials: string[];
  thumbnail: string;
  __v?: number;
};

export const CourseinitialUpdatingRow = {
  active: false,
  assessment: true,
  assessment_levels: [],
  certification_levels: [],
  course_completion: false,
  course_id: "",
  name: "",
  sample_report_card: "",
  sample_certificate: "",
  report_card: false,
  certificate: false,
  testimonials: [],
  thumbnail: "",
  __v: 0,
  _id: "",
};

export default function ActiveCoursesList(props: any) {
  const [rows, setRows] = React.useState<String[]>([]);
  const [searchRows, setSearchRows] = React.useState<String[]>([]);
  const [updateCourse, setUpdateCourse] = React.useState(false);
  const [updatingRow, setUpdatingRow] = React.useState<CourseData>(
    CourseinitialUpdatingRow
  );
  const [loading, setLoading] = React.useState<boolean>(false);
  const user = useSelector((state: any) => state.auth);

  React.useEffect(() => {
    if (!props.search) {
      setSearchRows(rows);
    } else {
      const filteredRows = rows.filter((row: any) =>
        row.name.toLowerCase().includes(props.search.toLowerCase()) ||
        row.course_id.toString().includes(props.search)
      );
      setSearchRows(filteredRows);
    }
  }, [props.search, rows]);

  React.useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    setLoading(true);
    getCourses()
      .then((data) => {
        
        if (!data) {
          ERROR_TOAST("Error Connecting to Server !");
          setRows([]);
          setSearchRows([]);
        } else {
          const modifiedData = data.map((course: any) => ({
            ...course,
            report_card: !!course.sample_report_card,
            certificate: !!course.sample_certificate,
            thumbnail: course.thumbnail || "",
          }));
          setRows(modifiedData);
          setSearchRows(modifiedData);
        }
        setLoading(false);
      })
      .catch((e) => {
        return ERROR_TOAST("Error Connecting to Server !");
      });
  };

  const handleDelete = async (row: any) => {
    if (window.confirm("Are you sure you want to delete this Course?")) {
      try {
        await deleteCourse(row._id);
        
        SUCCESS_TOAST("Course deleted successfully! ✅");
        getData();
      } catch (error) {
        console.error("Failed to delete Course:", error);
        ERROR_TOAST("Failed to delete Course ❌");
      }
    }
  };

  function getChipProps(params: GridRenderCellParams): ChipProps {
    if (params.value == false) {
      return {
        label: "Inactive",
        style: {
          background: red[700],
          width: 500,
          height: "30px",
          color: "#fff",
          fontFamily: "Poppins",
          fontStyle: "normal",
          fontWeight: 500,
          fontSize: "11px",
          lineHeight: "16px",
          borderColor: red[700],
        },
      };
    } else {
      return {
        label: "Active",
        style: {
          borderColor: "#5ED7C3",
          width: 500,
          color: "#fff",
          fontFamily: "Poppins",
          fontStyle: "normal",
          fontWeight: 500,
          fontSize: "11px",
          lineHeight: "16px",
          background: "#5ED7C3",
        },
      };
    }
  }

  function getChipProps2(params: GridRenderCellParams): ChipProps {
    if (params.value == false) {
      return {
        icon: <ReportOutlinedIcon style={{ fill: "#fff" }} />,
        label: "Pending",
        style: {
          background: "#FFB74A",
          width: 600,
          height: "30px",
          color: "#fff",
          fontFamily: "Poppins",
          fontStyle: "normal",
          fontWeight: 500,
          fontSize: "11px",
          lineHeight: "16px",
          borderColor: "#FFB74A",
        },
      };
    } else {
      return {
        icon: <CheckCircleOutlinedIcon style={{ fill: "#fff" }} />,
        label: "Sent",
        style: {
          borderColor: "#7A9BF9",
          width: 500,
          color: "#fff",
          fontFamily: "Poppins",
          fontStyle: "normal",
          fontWeight: 500,
          fontSize: "11px",
          lineHeight: "16px",
          background: "#7A9BF9",
        },
      };
    }
  }

  // Base columns visible to all users
  const baseColumns = [
    {
      field: "_id",
      hide: true,
    },
    {
      field: "course_id",
      headerName: "ID",
      width: 90,
    },
    {
      field: "name",
      headerName: "Name",
      width: 130,
      editable: true,
    },
    {
      field: "assessment",
      headerName: "Assesments",
      valueFormatter: (params) => (params.value ? "Yes" : "No"),
      dataGeneratorUniquenessEnabled: true,
      width: 90,
      editable: false,
    },
    {
      field: "assessment_levels",
      headerName: "Ass Levels",
      valueFormatter: (params) => params.value?.length || 0,
      width: 100,
      editable: false,
    },
    {
      field: "certification_levels",
      headerName: "Cert Level",
      valueFormatter: (params) => params.value?.length || 0,
      width: 100,
      editable: false,
    },
    {
      field: "report_card",
      headerName: "Report Card ",
      type: "boolean",
      renderCell: (params: any) => {
        return (
          <Chip variant="outlined" size="small" {...getChipProps2(params)} />
        );
      },
      width: 100,
      editable: false,
    },
    {
      field: "certificate",
      headerName: "Certificate ",
      type: "boolean",
      renderCell: (params: any) => {
        return (
          <Chip variant="outlined" size="small" {...getChipProps2(params)} />
        );
      },
      width: 100,
      editable: false,
    },
    {
      field: "course_completion",
      headerName: "Completion ",
      valueFormatter: (params) => (params.value === true ? "Yes" : "No"),
      width: 100,
      editable: false,
    },
    {
      field: "active",
      headerName: "Status ",
      description: "Status",
      renderCell: (params: any) => {
        return (
          <Chip variant="outlined" size="small" {...getChipProps(params)} />
        );
      },
      width: 100,
      editable: false,
    },
    {
      field: "edit",
      headerName: "Edit",
      width: 50,
      renderCell: (cellValues: any) => {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <EditRoundedIcon
              data-testid="EditRoundedIcon"
              style={{ color: "#8E8EA1", cursor: "pointer" }}
              onClick={(event) => {
                handleCellClick(event, cellValues);
              }}
            />

          </div>
        );
      },
    },
  ];

  // Action columns visible only to superusers
  const actionColumns = [

    {
      field: "delete",
      headerName: "Delete",
      width: 100,
      renderCell: (cellValues: any) => {
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <DeleteRoundedIcon
              data-testid="DeleteIcon"
              style={{ color: "#8E8EA1", cursor: "pointer" }}
              onClick={() => handleDelete(cellValues.row)}
            />

          </div>
        );
      },
    },
  ];

  // Combine columns based on user.isSuper
  const columns = user.isSuper ? [...baseColumns, ...actionColumns] : baseColumns;

  const handleCellClick = (
    e: React.MouseEvent,
    value: GridRenderCellParams
  ) => {
    const { row } = value;
    setUpdateCourse(true);
    setUpdatingRow(row);
  };

  const handelClose = () => {
    getData();
    setUpdateCourse(false);
  };

  return (
    <>
      {searchRows.length === 0 && props.search ? (
        <p className="no-results">🚫 No results found</p>
      ) : (
        <MainTable
          rows={searchRows.length > 0 ? searchRows : rows}
          columns={columns}
          loading={loading}
        />
      )}
      {updateCourse && <AddCourse handelClose={handelClose} data={updatingRow} />}
    </>
  );
}